import { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import CptTable from './CptTable';
import { peopleColumns } from './CptTable/columns/people';
import { apiFetch, fetchTableRows } from '../scripts/api';
import { splitValues, downloadCsv } from '../scripts/exportCsv';
import { __ } from '@wordpress/i18n';

const taxNames = ( connection ) =>
	connection?.nodes?.map( ( n ) => n.name ).join( '; ' ) ?? '';

function exportPeopleToCSV( rows ) {
	const maxEmails = rows.reduce(
		( max, p ) => Math.max( max, splitValues( p.contactMail ).length ),
		0
	);

	const emailHeaders = Array.from( { length: maxEmails }, ( _, i ) =>
		i === 0 ? __( 'Email', 'anacrm' ) : `Email ${ i + 1 }`
	);

	const headers = [
		__( 'Name', 'anacrm' ),
		__( 'Gender', 'anacrm' ),
		__( 'Birth year', 'anacrm' ),
		__( 'Employment status', 'anacrm' ),
		__( 'Permission', 'anacrm' ),
		...emailHeaders,
		__( 'Phone', 'anacrm' ),
		__( 'Interests', 'anacrm' ),
		__( 'Municipality', 'anacrm' ),
		__( 'Territory', 'anacrm' ),
		__( 'Region', 'anacrm' ),
		__( 'Sector', 'anacrm' ),
		__( 'Organisation', 'anacrm' ),
		__( 'Administration', 'anacrm' ),
	];

	const dataRows = rows.map( ( p ) => {
		const emails = splitValues( p.contactMail );
		while ( emails.length < maxEmails ) {
			emails.push( '' );
		}

		return [
			p.title ?? '',
			taxNames( p.genders ),
			p.yearBirth ?? '',
			taxNames( p.workConditions ),
			p.permissionContact ? __( 'Yes', 'anacrm' ) : __( 'No', 'anacrm' ),
			...emails,
			p.contactPhone ?? '',
			taxNames( p.interests ),
			taxNames( p.cities ),
			taxNames( p.territories ),
			taxNames( p.regions ),
			taxNames( p.sectors ),
			p.memberOrgs?.join( '; ' ) ?? '',
			p.memberAdmins?.join( '; ' ) ?? '',
		];
	} );

	downloadCsv(
		headers,
		dataRows,
		`personas-${ new Date().toISOString().slice( 0, 10 ) }.csv`
	);
}

export default function PersonTable() {
	const [ data, setData ] = useState( [] );
	const [ loading, setLoading ] = useState( true );
	const [ error, setError ] = useState( null );
	const [ emailList, setEmailList ] = useState( null );
	const [ shareUrl, setShareUrl ] = useState( null );
	const [ copied, setCopied ] = useState( false );

	useEffect( () => {
		fetchTableRows( 'person' )
			.then( ( rows ) => setData( rows ) )
			.catch( ( err ) => setError( err.message ) )
			.finally( () => setLoading( false ) );
	}, [] );

	const handleDelete = async ( databaseId ) => {
		try {
			await apiFetch( `/anacrm/v1/person/${ databaseId }`, {
				method: 'DELETE',
			} );
			setData( ( prev ) =>
				prev.filter( ( item ) => item.databaseId !== databaseId )
			);
		} catch ( err ) {
			window.alert(
				__( 'Could not delete the person:', 'anacrm' ) + err.message
			);
		}
	};

	useEffect( () => {
		const handler = () => setShareUrl( window.location.href );
		document.addEventListener( 'anacrm:share-person', handler );
		return () =>
			document.removeEventListener( 'anacrm:share-person', handler );
	}, [] );

	const handleCopyLink = useCallback( () => {
		navigator.clipboard.writeText( shareUrl ).then( () => {
			setCopied( true );
			setTimeout( () => setCopied( false ), 2000 );
		} );
	}, [ shareUrl ] );

	function handleEmailExport( rows ) {
		const emails = rows
			.map( ( p ) => splitValues( p.contactMail )[ 0 ] )
			.filter( Boolean )
			.join( ', ' );
		setEmailList( emails );
	}

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const columns = useMemo(
		() => peopleColumns( { onDelete: handleDelete } ),
		[]
	);

	if ( error ) {
		return (
			<div className="px-4 py-3 border-2 border-red text-red text-sm">
				{ __( 'Error loading data:', 'anacrm' ) } { error }
			</div>
		);
	}

	const filters = [
		{
			columnId: 'territories',
			label: __( 'Territory', 'anacrm' ),
			getTerms: ( row ) =>
				row.territories?.nodes?.map( ( n ) => n.name ) ?? [],
		},
		{
			columnId: 'cities',
			label: __( 'Municipality', 'anacrm' ),
			getTerms: ( row ) =>
				row.cities?.nodes?.map( ( n ) => n.name ) ?? [],
			allowedValues: window.anacrm?.allowedArchiveCities ?? null,
			dependsOnColumnId: 'territories',
			relationMap: window.anacrm?.cityTerritoryMap ?? {},
			disabledWhenValues:
				window.anacrm?.cityFilterDisabledTerritories ?? [],
		},
		{
			columnId: 'sectors',
			label: __( 'Sector', 'anacrm' ),
			getTerms: ( row ) =>
				row.sectors?.nodes?.map( ( n ) => n.name ) ?? [],
		},
		{
			columnId: 'memberOrgs',
			label: __( 'Organisation', 'anacrm' ),
			getTerms: ( row ) => row.memberOrgs ?? [],
		},
		{
			columnId: 'memberAdmins',
			label: __( 'Administration', 'anacrm' ),
			getTerms: ( row ) => row.memberAdmins ?? [],
		},
		{
			columnId: 'memberPositions',
			label: __( 'Position', 'anacrm' ),
			getTerms: ( row ) => row.memberPositions ?? [],
		},
		{
			columnId: 'genders',
			label: __( 'Gender', 'anacrm' ),
			getTerms: ( row ) =>
				row.genders?.nodes?.map( ( n ) => n.name ) ?? [],
		},
		{
			columnId: 'workConditions',
			label: __( 'Employment status', 'anacrm' ),
			getTerms: ( row ) =>
				row.workConditions?.nodes?.map( ( n ) => n.name ) ?? [],
		},
		{
			columnId: 'interests',
			label: __( 'Interests', 'anacrm' ),
			getTerms: ( row ) =>
				row.interests?.nodes?.map( ( n ) => n.name ) ?? [],
		},
		{
			columnId: 'permissionContact',
			label: __( 'Permission', 'anacrm' ),
			getTerms: ( row ) => [
				row.permissionContact
					? __( 'Yes', 'anacrm' )
					: __( 'No', 'anacrm' ),
			],
		},
	];

	const emailContainer = document.getElementById( 'anacrm-email-export' );
	const shareContainer = document.getElementById( 'anacrm-share-person' );

	return (
		<>
			<CptTable
				columns={ columns }
				data={ data }
				isLoading={ loading }
				filters={ filters }
				exportEvent="anacrm:export-person"
				onExportRows={ exportPeopleToCSV }
				emailExportEvent="anacrm:export-emails-person"
				onEmailExportRows={ handleEmailExport }
				searchPlaceholder={ __( 'Search people…', 'anacrm' ) }
				fitWidth
			/>
			{ emailList !== null &&
				emailContainer &&
				createPortal(
					<div className="w-full px-8 py-3 bg-green flex items-center justify-between">
						<span>{ emailList }</span>
						<button
							onClick={ () => setEmailList( null ) }
							className="px-3 py-2 text-2xl hover:text-yellow cursor-pointer"
						>
							&times;
						</button>
					</div>,
					emailContainer
				) }
			{ shareUrl !== null &&
				shareContainer &&
				createPortal(
					<div className="w-full px-8 py-3 bg-green flex items-center justify-between">
						<div className="flex flex-col gap-1 min-w-0">
							<strong>{ __( 'Share', 'anacrm' ) }</strong>
							<div className="flex items-center gap-3 flex-wrap">
								<span className="text-xs">
									{ __( 'Link to share:', 'anacrm' ) }{ ' ' }
									<a
										href={ shareUrl }
										className="underline break-all"
									>
										{ shareUrl }
									</a>
								</span>
								<button
									onClick={ handleCopyLink }
									className="shrink-0 px-3 py-2 border-2 border-green-dark bg-gray-light hover:bg-yellow cursor-pointer font-bold text-xs"
								>
									{ copied
										? __( 'Copied!', 'anacrm' )
										: __( 'Copy link', 'anacrm' ) }
								</button>
							</div>
						</div>
						<button
							onClick={ () => {
								setShareUrl( null );
								setCopied( false );
							} }
							className="px-3 py-2 text-2xl hover:text-yellow cursor-pointer shrink-0"
						>
							&times;
						</button>
					</div>,
					shareContainer
				) }
		</>
	);
}
